import { ActivatedRoute } from '@angular/router'
import { CommonModule, DatePipe } from '@angular/common'
import { Component, OnInit } from '@angular/core'
import { QuotationModel } from '../models/quotation.model'
import { QuotationService } from '../quotation.service'
import { ToastrService } from 'ngx-toastr'
import { MatDialog } from '@angular/material/dialog'
import { NavigationExtras, Router } from '@angular/router'
import { FormsModule } from '@angular/forms'
import { QuotationItem } from '../models/quotation_item.model'
import { InvoiceData } from '../quotation_invoice_data'
import { MasterCommonServices } from '../master.common.service'
import { AuthStateService } from '../../../auth-state.service'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import { ConfirmationDialogComponent } from '../../../shared/components/confirmation-dialog/confirmation-dialog.component'
import pdfMake from 'pdfmake/build/pdfmake'
import 'pdfmake/build/pdfmake.js'
import 'pdfmake/build/vfs_fonts.js'
import { TDocumentDefinitions } from 'pdfmake/interfaces'
import { CreateWorkOrderRemarkDialogComponent } from './create-work-order-remark-dialog/create-work-order-remark-dialog.component'
import { WorkOrderService } from '../../workOrder/workOrder.service'
import { WorkOrderModel } from '../../workOrder/models/workOrder.model'
import { WorkOrderItem } from '../../workOrder/models/workOrder_item.model'
@Component({
  selector: 'app-view-all-quotations',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './viewAllQuotations.component.html',
  styleUrls: ['./viewAllQuotations.component.css'],
  providers: [DatePipe],
})
export class ViewAllQuotationsComponent implements OnInit {
  quotationData: QuotationModel[] = []
  quotationStageFilteredData: QuotationModel[] = []
  filteredQuotationData: QuotationModel[] = []
  selectedStage: string = 'All'
  financialYears: string[] = []
  selectedFinancialYear: string = 'all'
  permissions: string[] = []
  stages: string[] = []
  permissionStageMap: { [key: string]: string } = {
    QuotationsDraftListPermission: 'draft',
    QuotationsPublishedListPermission: 'publish',
    QuotationsProformaListPermission: 'proforma',
  }

  quotationCommonServices: MasterCommonServices | undefined

  filter = {
    quoteId: '',
    assignedTo: '',
    date: '',
    client: '',
    value: '',
    state: '',
    importance: '',
  }

  constructor(
    private datePipe: DatePipe,
    private masterCommonService: MasterCommonServices,
    private quotationService: QuotationService,
    private toastr: ToastrService,
    private dialog: MatDialog,
    private router: Router,
    private authStateService: AuthStateService,
    private workOrderService: WorkOrderService,
  ) {
    this.quotationCommonServices = masterCommonService
  }

  filterByStage(): void {
    this.quotationStageFilteredData = this.quotationData?.filter(
      (quot) =>
        this.selectedStage.toLowerCase() === 'all' ||
        (quot.quotationFields.quotationStage.toLowerCase() || 'draft') ===
        this.selectedStage.toLowerCase(),
    )
    this.filterByFinancialYear()
  }
  ngOnInit(): void {
    if (this.masterCommonService.isDataLoaded == false) {
      this.masterCommonService.getAllMasters()
    }
    this.getAllQuotations()
    this.performFinancialYearOperations()
    this.authStateService.permissions.subscribe((permList) => {
      this.permissions = permList
      this.initializeStagesBasedOnPermissions()
    })
  }

  initializeStagesBasedOnPermissions(): void {
    const availableStages = Object.entries(this.permissionStageMap)
      .filter(([permKey]) => this.permissions.includes(permKey))
      .map(([_, stage]) => stage)

    this.stages =
      availableStages.length > 1
        ? ['All', ...availableStages]
        : [...availableStages]
    this.selectedStage = this.stages[0]
  }

  hasPermission(action: string): boolean {
    return this.permissions.includes(action)
  }

  applyFilters() {
    this.filteredQuotationData = this.quotationStageFilteredData
    this.filterByFinancialYear()

    this.filteredQuotationData = this.filteredQuotationData.filter((quot) => {
      return (
        (!this.filter.quoteId ||
          quot.quotationFields.quotationId
            .toString()
            .includes(this.filter.quoteId)) &&
        (!this.filter.assignedTo ||
          this.quotationCommonServices
            ?.getAssignedToName(quot.quotationFields.quotationAssignedToId)
            ?.username.toLowerCase()
            .includes(this.filter.assignedTo.toLowerCase())) &&
        (!this.filter.date ||
          new Date(quot.quotationFields.quotationDate)
            .toLocaleDateString('en-GB')
            .includes(this.filter.date)) &&
        (!this.filter.client ||
          this.quotationCommonServices
            ?.getClientName(quot.quotationFields.quotationCompanyId)
            ?.companyName.toLowerCase()
            .includes(this.filter.client.toLowerCase())) &&
        (!this.filter.value ||
          quot.quotationFields.grandTotal
            .toString()
            .includes(this.filter.value)) &&
        (!this.filter.state ||
          this.quotationCommonServices
            ?.getClientName(quot.quotationFields.quotationCompanyId)
            ?.region.toLowerCase()
            .includes(this.filter.state.toLowerCase())) &&
        (!this.filter.importance ||
          quot.quotationFields.quotationImportance
            .toLowerCase()
            .includes(this.filter.importance.toLowerCase()))
      )
    })
  }

  resetFilters(): void {
    this.filter = {
      quoteId: '',
      assignedTo: '',
      date: '',
      client: '',
      value: '',
      state: '',
      importance: '',
    }
    this.filteredQuotationData = this.quotationStageFilteredData
  }

  getAllQuotations(): void {
    this.masterCommonService.isLoading = true
    this.quotationService.getAllQuotations().subscribe({
      next: (data: QuotationModel[]) => {
        this.quotationData = data
        const allowedStages = this.stages.filter(
          (stage) => stage.toLowerCase() !== 'all',
        )


        this.quotationData = this.quotationData.filter((q) =>
          allowedStages.includes(
            q.quotationFields.quotationStage.toLowerCase(),
          ),
        )

        this.filterByStage()
        this.masterCommonService.isLoading = false
      },
      error: (error: any) => {
        this.masterCommonService.isLoading = false
        this.toastr.error(error.statusText, 'Error', {
          timeOut: 15000,
          closeButton: true,
          progressBar: true,
        })
      },
    })
  }

  getQuotationId(id: string): string {
    const fullFinancialYear = this.getCurrentFinancialYear()
    const shortFinancialYear = this.getShortFinancialYear(fullFinancialYear)
    return `ATF/${id}/${shortFinancialYear}`
  }

  getShortFinancialYear(financialYear: string): string {
    const [startYear, endYear] = financialYear.split('-')
    return `${startYear.slice(-2)}-${endYear.slice(-2)}`
  }

  editQuotation(quot: QuotationModel) {
    const navigationExtras: NavigationExtras = {
      state: {
        mode: 'update',
        quotationFields: JSON.stringify(quot.quotationFields),
        quotationItems: JSON.stringify(quot.quotationItems),
        quotationTerms: JSON.stringify(quot.quotationTerms),
      },
    }
    this.router.navigate(['/editQuotation'], navigationExtras)
  }

  viewQuotationDetails(quot: QuotationModel) {
    const navigationExtras: NavigationExtras = {
      state: {
        mode: 'update',
        quotationFields: JSON.stringify(quot.quotationFields),
        quotationItems: JSON.stringify(quot.quotationItems),
        quotationTerms: JSON.stringify(quot.quotationTerms),
      },
    }
    this.router.navigate(
      ['/viewQuotationDetails', quot.quotationFields.id],
      navigationExtras,
    )
  }

  performFinancialYearOperations(): void {
    this.initializeFinancialYears()
    this.filterByFinancialYear()
  }

  initializeFinancialYears(): void {
    const currentYear = new Date().getFullYear()
    const currentFinancialYear = this.getCurrentFinancialYear()
    this.financialYears = [
      currentFinancialYear,
      `${currentYear - 1}-${currentYear}`,
      `${currentYear - 2}-${currentYear - 1}`,
      `${currentYear - 3}-${currentYear - 2}`,
      `${currentYear - 4}-${currentYear - 3}`,
    ]
  }

  getCurrentFinancialYear(): string {
    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().getMonth()
    return currentMonth >= 3
      ? `${currentYear}-${currentYear + 1}`
      : `${currentYear - 1}-${currentYear}`
  }

  filterByFinancialYear(): void {
    if (this.selectedFinancialYear === 'all') {
      this.filteredQuotationData = this.quotationStageFilteredData
    } else {
      const [startYear, endYear] = this.selectedFinancialYear
        .split('-')
        .map(Number)
      const startDate = new Date(startYear, 3, 1)
      const endDate = new Date(endYear, 2, 31)



      this.filteredQuotationData = this.quotationStageFilteredData.filter(
        (quot) => {
          const quotationDate = new Date(quot.quotationFields.quotationDate)
          return quotationDate >= startDate && quotationDate <= endDate
        },
      )
    }
  }

  exportToExcel(): void {
    const exportData = this.filteredQuotationData.map((quot) => ({
      'Quote #': quot.quotationFields.quotationId,
      'Assigned To':
        this.quotationCommonServices?.getAssignedToName(
          quot.quotationFields.quotationAssignedToId,
        )?.username || '-',
      Date: quot.quotationFields.quotationDate
        ? new Date(quot.quotationFields.quotationDate).toLocaleDateString()
        : '-',
      Client:
        this.quotationCommonServices?.getClientName(
          quot.quotationFields.quotationCompanyId,
        )?.companyName || '-',
      Value: quot.quotationFields.grandTotal,
      Country:
        this.quotationCommonServices?.getCountryName(
          quot.quotationTerms.countryofOriginId,
        ) || '-',
      Importance: quot.quotationFields.quotationImportance,
      Status: quot.quotationFields.quotationStatus,
    }))

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData)
    const workbook: XLSX.WorkBook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Quotations')

    const excelBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    })
    const data: Blob = new Blob([excelBuffer], {
      type: 'application/octet-stream',
    })

    saveAs(data, 'Quotations.xlsx')
  }

  deleteQuotation(quotation: QuotationModel) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: { message: 'Are you sure you want to delete this quotation?' },
    })
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.quotationService.deleteQuotation(quotation).subscribe({
          next: (response) => {
            this.toastr.success('Quotation Deleted Successfully!', 'Success')
            this.getAllQuotations()
            this.applyFilters()
          },
          error: (error) => {
            this.toastr.error(error.statusText, 'Error', {
              timeOut: 15000,
              closeButton: true,
              progressBar: true,
            })
          },
        })
      }
    })
  }

  createWorkOrder(quot: QuotationModel): void {
    const dialogRef = this.dialog.open(CreateWorkOrderRemarkDialogComponent, {
      width: '400px',
      data: {},
    })

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return
      const now = new Date(
        new Date().getTime() - new Date().getTimezoneOffset() * 60000,
      ).toISOString()

      const payload: WorkOrderModel = {
        workOrderFields: {
          workOrderId: '', // optional for creation, API might generate this
          workOrderCompanyId: quot.quotationFields.quotationCompanyId,
          purchaseOrderNumber: result.purchaseOrderNumber || '',
          workOrderDate: new Date().toISOString().split('T')[0],
          remarks: result.remarks,
          status: 'ongoing',
          addedOn: now,
          modifiedOn: now,
          isDeleted: false,
          modifiedBy: null,
          addedBy: '',
        },
        workOrderItems: quot.quotationItems.map(
          (item, index): WorkOrderItem => ({
            workOrderId: 0, // backend will map it after creation
            srNo: (index + 1).toString(),
            partNumberId: null,
            description: item.description,
            quantity: item.quantity,
            dispatchedQuantity: 0,
            unit: item.unit,
            addedOn: now,
            modifiedOn: now,
          }),
        ),
      }

      this.workOrderService.addWorkOrder(payload).subscribe({
        next: () => {
          this.toastr.success('Work Order created successfully!', 'Success')
        },
        error: (err: any) => {
          this.toastr.error('Failed to create Work Order', 'Error')
        },
      })
    })
  }

  viewQuotation(quot: QuotationModel): void {
    this.generateInvoice(quot)
  }
  generateInvoice(data: QuotationModel): void {
    const logoBase64: string =
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAARsAAACyCAMAAABFl5uBAAABCFBMVEX///8AAeUAAOUAAOQBYwAAWQAAXAD8/fz0+fT8/P+0yrSSsZKiovP19f4AYADq6v0mJueurvfh4fzV1ftNTuyBgfEvMetBQunw8P35+f8NDeaFhvK/wPe4ufczNOlSU+qbm/N8fPClpfPKyvvNzfhra+9aWuuWlvU9Pel2dvDc3PpmZu7Y5tg9hTwdHudwcO2OjvR5pXno8ugAagDe6t45Ouqty628vPiYmPRfX+1HR+kWbhUkeSOexZ54qXhtpmyOuY1VlFQpdSnF28U4hjhcmltOk03A2sCFtIRjnWLK2sqdvJ00iDKbw5uOtY4aaRp1nnVGgEZbj1sucy1RiVEhfCBsmmxYiVjVMrYIAAASYUlEQVR4nO2d+WOayhbHcSYavU8EwQVxwX3BXKRJjNG0TZfbNm1f09vX+/r+///kzZkZEFTUNIlowveHNiLCzIdZzpk5MwhCpEiRIkWKFGk7JU9OkmGnYU91+vWfP79GcFYpeXuVyBzdHoedjj1U8utV5ugoc/VHBGdRx+/fETQEzp+nYSdl73T6lqI5Okpch52UfVPyK0dDSs77sBOzZzp9OWfzV9iJ2S8dX8SPXMU/hZ2cvVLyz8ycTVRwfDr1FBtScJ65AXgMcj989bFJvFp92vNQ8uLDxw+vL86TrIQcf8542WTesqPJ0y+31x8/Xp+EmdRd6/hTPJHJJOKJl6/P6ef/+tm8hIPJT2/P4nBe/Ow5lZzj1wlOIX4DJef47MgvgHHyMpF5hh1X0mFzlHhN2SQW2IDf8MU1eeIXYSd4hzq+8bNJxhfYQAvjYfOcGpy5qbeGzYnLJvOc2hvhZIty47LJ/Dvs5O5UrvvE25sFNEfQe7l1KnETdnJ3qqTT4GQ+s34q42dD22LnYPw87OTuVl+c+vKRsvlrgQ09xWltXj6r5oZUKu5cZl5SNh/8tt8VnPKFF634qw3Xempye6ozqD7Hb3wGDnXE/Wc8Kzm9EGtM/vD7mtD4Jm8Zr8T1s/PKk9z1jtN5hYUxChgVPWX+Z+bsmbXEoNMX4C5l4t+ATfJDPOMqQe2Zc9oiZY6e5ejx6c3V9+/fXzJ/IPnmw/X19WfQ9Td25Ba+fvcs0YCSybXd8+nps2tqIkWK9FtK7V5hZ3krSa1qNr1zVQ4Ajtkc1RDCO9cg7IxvVnaIMEIotmMhVA475xtlW3jnXCgb/TLsrG9SefclhrPpV8LO+walY2GhQcNS2JlfL7MWWrGRs2FnfoM6oaFBw7DzvkFqWMUGoZoUduY3qKyHhAZbZth536RpGMWG2FJovO+lRtByaCHNq7SUsYWDq84LuhaVLu+/1SeYReTNji6vkOXnwM/xmIsIs/N037XQqmuBCn2jvPeFhkj1sCHlPN/MLuty6IWDu/SctOX5ocXOq3mvZTVWXQs0UcLO9XbysbGC7I0p9rApU4NNlOc/xAV6lpSbn4ZivV1l4dHkZYM7QQ9U9bLJi3BIW2ajeNlYe27ybiEfm7IWcFbprmwQKuwqB4+nR2ITi9hE5SZi88BsZElZIekAxoddPVp7oxsrlDsIm8/RY7FZ7TLgzsGysQNtkruzWaVDGDv3yG8XF4Lk9RnuwUavh5nXu0r1+5qBehA2OKeGmtk7ysdmCc6qwYffZ4OQHWpe7yo/m23022wQmh5SS7xDNqTw5dqhZvXO2hUbgqbbCjWnd9ejsVkwbFCxfCAjWnM9Fhuk9+cqGiP70AqN8GhsiD/VqsylBhnce60t7Zu7l5sn5oevMf3uavs9vTGKYbWyWpNo/KYsBpyVithE434+RWyC5R+/CapTgmeC12FTw2vZjHeUgUeUhw3C6cDT0BIbsbuejbGb9D+mvGxQcIhZES+yKdlr2eDDGo5YKQ8bPAv2k9PzSsXZCKbn0Ao2B+c9LWvOBiE7eIJEREtsPJEBy2xwfyepf1y5bND6YF/bLSUOm9TE5bXEBuEDdC2XNGejrw0K0foOHIeNII0cOItsEO48esJ3IIcNQqP1LUSrwEm4bAQz5xzys0F4dpB+96IYG+JMDjdMAaSqY1Zy5myEdo55oX42CB3WdEKgKBuE9fzGgNbUpIuAjoeNoHZ0TOh42ZBrjZ4GGsIGY4yG2W0qgWkXYYmVh42gpLtwiLOZwbW66cOaTQiW2e1Pe9Uto6DFdrNhFHpezyKlZsvdMTOClc54amf3PqJ6a4lq2wz0olZIUlvKghmkmW1WiUrkWk+iEY4UKVKkSJEiRYoUKVKkSJEi/ba0KmxDpAbPokjZehNGhFvNdPoht4FgN17Qith/OC2kXUtaOkQcjgKHT8Q6wigv0Q1dHnQeX+ni5UimFcmYkdNGD3jf7aWVWQoDR2qVHEa41qZ7LNxnHr8kqf4lzMp0Ozbk/iicHaRUulob+cZyfSKPN4b76n3ZaHUjV8h6ay65MBOdYeB/Pyab84vbF3///eLbtltzipcY1h7EUCxokilVjWFUF+/LRhqR4pf2stHqowGog0kCZgOmFU/ogdicvP7xDvZzPbr6+Z/t3gGhyMAFnlvgEqiU0jbhu3uzifnZCKImglQM6+VL9O9Vrd6DsDm++HmUcfYmz3x/vcVPSlmo8+URJG/TwPg969SAsGmu+kaBm9eDf/kQbI4voMRkEnF46QyUni2289fGJMWGWYeuyk242rvsmUJrKFuFkSqk2nbPrszrlFkeThvenQelXo6cOSStdd22bdKhKXavR9on6XJqdFinT7rryxmK4SHpjpuLZcPDxsw2m24y2ukmNS0Ym9RkWJPHnar3l5OprOv6OO+Z1qqMxpZV6NYXHvQp7OOfOPvw7eLbNfyZ+bl5F+kKFJu6oBokfTo/lmqSNjE9oPv2YTQq1WGnQYmx6acM1mbKTrhDiZxIz8RTZUy+IMfb5L+6lmfNKw1bUsdwToweQYuBEh429Rg5wUmGDfcVORu1wO+CXFOnUsOYL3kYLBwi//rDXm7ihMcvvhn7hwwpOG82oRGLJF1Fklgb5l8nLhuEdKdTHaTSpBFtcDZyDTtLMFgatZrbEeOcRf6lbBDuztwTbToHivjsOdLXsYErOGx6cF/GRpet+W3Yzn6pLJqbANigjWVV9lgFvuCOfxMc/7hvmvlOGp2Nb69UaWtDrlvpu3PTwIbngsComaU0qXYOG0iPBRmI4Rrt2KDAkaSPx3IM0R6PsomR54+wZelQWOSJoOZ02OIG6ZZlyYsxXsFsYpwNi3u3ZJleD9OnUolBUS/0i30ZvmzAhTqQuqJhFAvEWvTVKlKPMn+7n169efNmY1dFTBdUoFV4APlue9kUR71eZ9wQRA8biHDtXF7CDqIIV0mprQLcQqOlSZWRhTxsyNFBs9ozIN1TQZs0aXszIk3IuvYmiA1sijOsV6rlGTwfK0UNR0K90YZIBLhJkfw1IUgQba7UztBvkvwX2NxpY20FUs68hWyBJN1w2SBk0NnptuJjgzjJao2c3SE/hBpV4E7WpeVlU6M1VISH3odaRPuple7YVuVGrtNaY06hDJHLtKBcc5MgNSa1jtTcKvm1zJksRB1cJ44y7+703mnoui0W/ipOgZPpstHdku9nwyp7qQeYNKEELVPeycwQOe0NyQD3GlV4zpBxat9kV3m027CJlfkvYeM81BFE8iXKTRQTpJRZL1+B7+qqqUhLptp70vpm3t1+OfHofB0qjRZ4TjgNyRo5bLDhlkkfG4vX4izUfElokUTV3I4jbc3Z1JwWF4CW781m7N4EnmFRkKBKFXJcRTAPNNLeQIZko5NvVkz/nY5vocVJHL38c67P694CZsPznfKVXGmorJbisBm5hdLHxvHDq9AASgJYjjP3TAhe42xw1wkYgR4qf282hnsTSEVNkGY0mIkL4p66JOmtIuvAyRFjIS4oeXPGTOL5myF+rPGrRGgfsOVsgUZb2vIGNo5dzNk0A9kMHTazh2CDu+5N7BVsQFDQU61GAQ4CIMvvn3z5+wrIJKio77CWTRoh36owGg0r3YkNmI5j115pWvdlA1fAvNpqDU+5KbrNH9SbPqtT3Z5HWfozqZXtDYa0+PhCB09+JOCVRW9/vQDBW0S+/7oNNo1TfbQotu3DHdho0Ba7MfYjdF82lQLYBuxwC0wuh40bpquMEey0Cm0xuYfIRRxVt/kVJUWtQvvgdd4+EzRnv96fnIKSr0nb8+P9mndpZPVFNFAWDfEubGhL60ReZ+V1bGgxWLkZiYeNBE98xkpAA3nYoD4z2rWBPu/DrSZHLfGi267zVKdIUrw7Y5/Da4p+ue9vuiE9+o91DhVYTFaa9YKsK4SEydk7saHNeb+uQLhjwWP7LbMBtwRZWaW95It7/XBqBgyrkjQZwpibx/Yb221NyQ6p7UfKiGJganSWhJTaM2b08Wh53WDhlBVq/cxvAa+3uvrqftzEpgK58w85UCO3Q2r99mxEHeAQOz3X170+wwo2tIeXi7XCYjCol02L2o+FIlj9vnJD8lqbFWnJZJVuQpuA2szIjcmFixPwsMDIKE7z5SEYskWP3/YqTqybuZdwk1nPhj6huv8Y3K5WvQsbYtthpz3HRX1NnSLNJztNX1xr5Ru/yWO+BQoGD8z1Na15k8jXEJXSHl+Tmn6lS34D6nVRD8vRJ3jl1a378S/C5mfwu3HbNRioXTgIoxHkLjBGMd/CSYSjwAYGHVz7xiJn0lNU3RkWKHrHKKZz+wbzZKp9OBPj3BIbctxtHPJ8LEKvksxTNjO4Ave6+ZgHKEXTwA/WGa6YgwvjodfXhHd6Zq7Y+5yE07d09O9tYMFJG7PcbGm1XDGXmw3MSa5r1F3TqVQdF/uX5GPWmM2cgZLWcDbrslNSzTGkrtAsCY3udEqaHnVmGLbD1jaMLr9PqV7U9UJnca5JGudmxnzMirpMBfL7VrdfAy/KzhlT4soPIZB93PO4A2KTRnSjopsPqawza2fsGwOjJQXeRXj96uLNRzoCCKx28+ZgSQlrD9CUthiz3K5WlyciT+hYMYyJUsMvA7Xq3W7Y7L8uvmfcV1dmjt4mzzIRG1ef/vnJ3amrn68Jq59HERtXyU83v/5H9OsN9b+/3Lx4tq/XW61kMnqrXqRIkSKtk9m8rHt1SZxs89JeOYc+V6pp957KUsZgTcYx3Stka+KAuI7Vtb9Ky3Ts4ImrWvM61sRfLGsteM1gl32tqZ4ljSXFWcPYBc/7yRccmBCM+dmoEOZB3zXVns1qrv8q5nL9KR9BhpGP2OG/0WGDgA2y5tJ7mmAjPhRSwYgGc1CJMCLCh65UC6Pg3XeeiuhEcsv/6tWUxitShQdzUImkrLjDeillu9XGh7QX/ZIom6CNdFrAxhlTKnnZbKfSIHfIy89XsSkb3ZwqtEcNiPgYjxqDkSQOGhD0YU3Jh0pJmEy7RpkUpexgUJcEqZ4rFBueGAqzodPBzDrGMu3wUpUhDLCO7YPagmgVG4gSywppHmgLI3qqwiZ56QfSIvXIH31BkIYYjytNPuw34I2zaCN32BQhCBNWc25kV+CehHuoVWy65FCVTi3zqC9gg5wJWcpGh9gBQepgJMvO8DruUjhaw+304N2KCkXDR9tR4Hsoj/dOnM1EcgWjnYxNdTyDPkwuko7clIozmEjTx7NZvyl62MToUZgMQmweNAUhnqg27Uwh+IRGusAkWaFTLg+6VtDO/ed/7J3OGRtj6Aq2dGZsNFVtwusrWqaqllKq2SY57GfJB9JxedjEUK1cMds2zA9BBEMb5n8MGl4GM31ThU88AwGx3gswii6u4numswtm+3kiI/pth43A+qm8t59ytobzlhurSfOb5RUG/q+xiAFTphNjCjkLb1hTc/HOHVjeE11dLNrFuKhSj4CxAfsm77VvZq0lNq5xKGOklwXNJr+55Nufwd/lVKlMS1yltWYLFlJuEnslp9x4/amZ6ik3FZL1DWzmOzB2aIybMsLIDWZus7naagHDvKBcnNrVACfs5Ou/9kxfT1h706u6qmg+NhvLDUKOx55nbOCQ0++ZhE1HImaQG51tBWxRf5zcOx0zNgvLuO7DRhpgFugrwAQxD3QS272czmO7gjeI3TsF2zfCKjZLbfEim1Ids0BfohSs8WPxTZrZbreaEGFzQG/EWMum5ffD5/5UMBsBordil7TgQMxvDb7mk9MlsxBs/O2h1rIBkwYWRtKcioSHY7qtYWNCUKA1TU+aU3ChGgRLezpgrUxJfzpsVBpKNZz2oY6UoIbI3SEEvK9hI2QtGjkl0+gdiJqBxTm1TtaUKgYYCSGtHv4NrWUDxgrtX2hVqvAPtraWjVCfxy/RCOUsXU4DK07gUONwhnSqNXe5iSvX9mPLjMinIWRIbLAPY3M9G6FVZMHZvPFO9eaw8CEN6FTHSF9kMyWHeI4lGiKqD+jDTsH0AoqR7F2SojCjbHSXTZn87URRSc38qEtXN0HkutA2eHisPnhaGwoqqseWlVRz+zoBvkKszn6XLQ9G+a22+nwm6lB/KuxU7I1Skqm4LuUEXNGorDhSYA2B001nuc8QiUqEcQkeJ62BRfQEXm7wYKpCl2S3JVFqg89gbYg6eFaigxSo37DzdNHQ9KBmXR5brRmLcafx8OPD8RB2oVJr6sxPIaMV9eB+KdURrKiRh09ow/EHEzFyVFU1D+qt9ZEiRXrS+j+scyIK9ca7IAAAAABJRU5ErkJggg==' // Your logo data

    // Define theme colors
    const themeColors = {
      primary: '#488cec',
      primaryText: '#FFFFFF',
      secondary: '#f0f0f0',
      accent: '#e6e6e6',
      border: '#cccccc',
    }

    const termsList = Object.entries({
      'DELIVERY NAME': this.masterCommonService.getDeliveredToName(
        data.quotationTerms.deliveryNameId,
      ),
      'CURRENCY': this.masterCommonService.getCurrencyName(
        data.quotationTerms.currencyId,
      ),
      'DELIVERY TIME': this.masterCommonService.getDeliveryTimeName(
        data.quotationTerms.deliveryTimeId,
      ),
      'COUNTRY OF ORIGIN': this.masterCommonService.getCountryName(
        data.quotationTerms.countryofOriginId,
      ),
      'PAYMENT': this.masterCommonService.getPaymentTypeName(
        data.quotationTerms.paymentId,
      ),
      'MTC TYPE': this.masterCommonService.getMtcTypeName(
        data.quotationTerms.mtcTypeId,
      ),
      'QUOTATION VALIDITY': this.masterCommonService.getValidityName(
        data.quotationTerms.validityId,
      ),
      'PACKING TYPE': this.masterCommonService.getPackingTypeName(
        data.quotationTerms.packingTypeId,
      ),
    })
      .filter(([_, value]) => !!value && value !== '-')
      .map(([key, value]) => [
        {
          text: [
            { text: `${key}: `, bold: true, fontSize: 10 },
            { text: `${value}`, fontSize: 9 },
          ],
          margin: [0, 2, 0, 2],
        },
      ])

    const termsAndConditionsSection = termsList.length
      ? {
        table: {
          body: [
            [
              {
                text: 'TERMS & CONDITIONS',
                alignment: 'left',
                bold: true,
                color: themeColors.primary,
              },
            ],
            ...termsList,
          ],
        },
        layout: 'noBorders',
      }
      : null

    const docDefinition: TDocumentDefinitions = {
      content: [
        // Header section - unbreakable
        {
          unbreakable: true,
          stack: [
            {
              columns: [
                {
                  width: '*',
                  stack: [
                    {
                      image: logoBase64,
                      width: 100,
                      margin: [0, 0, 0, 10],
                    },
                    {
                      text: InvoiceData.ADDRESS,
                      fontSize: 11,
                      margin: [0, 0, 0, 5],
                    },
                    {
                      text: data.quotationFields.isGstRemoved
                        ? InvoiceData.IEC
                        : InvoiceData.GSTNO,
                      fontSize: 11,
                      margin: [0, 0, 0, 5],
                    },

                  ],
                },
                {
                  width: 250,
                  stack: [
                    {
                      text:
                        data.quotationFields.quotationStage.toLowerCase() ==
                          'proforma'
                          ? 'PROFORMA INVOICE'
                          : 'QUOTATION',
                      fontSize: 20,
                      bold: true,
                      alignment: 'right',
                      margin: [0, 0, 0, 10],
                      color: themeColors.primary,
                    },
                    // Quote # and Date table
                    {
                      table: {
                        widths: ['*', '*'],
                        body: [
                          [
                            {
                              text: 'QUOTE #',
                              bold: true,
                              alignment: 'left',
                              fillColor: themeColors.secondary,
                            },
                            {
                              text: 'DATE',
                              bold: true,
                              alignment: 'right',
                              fillColor: themeColors.secondary,
                            },
                          ],
                          [
                            {
                              text: String(data.quotationFields.quotationId),
                              fontSize: 9,
                              alignment: 'left',
                              bold: true
                            },
                            {
                              text:
                                this.datePipe.transform(
                                  data.quotationFields.quotationDate,
                                  'dd/MM/yyyy',
                                ) || '-',
                              alignment: 'right',
                              fontSize: 9
                            },
                          ], 
                        ],
                      },
                      layout: 'noBorders',
                    },
                    // Customer section
                    {
                      table: {
                        widths: ['*'],
                        body: [
                          [
                            {
                              text: 'CLIENT',
                              bold: true,
                              fillColor: themeColors.secondary,
                            },
                          ],
                          [
                            {
                              text:
                                this.masterCommonService.getClientName(
                                  data.quotationFields.quotationCompanyId,
                                )?.companyName || '-',
                              bold: true,
                              fontSize: 9
                            },
                          ],
                          [
                            {
                              text:
                                this.masterCommonService.getClientName(
                                  data.quotationFields.quotationCompanyId,
                                )?.address || '',
                              fontSize: 8
                            },
                          ],
                          [
                            {
                              text:
                                this.masterCommonService.getMemberName(
                                  data.quotationFields.quotationAttentionId,
                                )?.memberName || '',
                              fontSize: 8
                            },
                          ],
                          [{ text: '', height: 10 }],
                          [
                            {
                              text: 'REFERENCE',
                              bold: true,
                              fillColor: themeColors.secondary,
                            },
                          ],
                          [
                            {
                              text: data.quotationFields.reference || '',
                              fontSize: 9
                            },
                          ],
                        ],
                      },
                      layout: 'noBorders',
                      margin: [0, 10, 0, 10],
                    },
                  ],
                },
              ],
            },
          ],
        },
        // Items table - unbreakable
        {
          unbreakable: false,
          stack: [
            {
              table: {
                headerRows: 1,
                widths: ['auto', 300, 'auto', 'auto', 'auto', 'auto'],
                body: [
                  [
                    {
                      text: 'Sr No',
                      bold: true,
                      fillColor: themeColors.primary,
                      color: themeColors.primaryText,
                      alignment: 'center',
                    },
                    {
                      text: 'Description',
                      bold: true,
                      fillColor: themeColors.primary,
                      color: themeColors.primaryText,
                      alignment: 'center',
                    },
                    {
                      text: 'Quantity',
                      bold: true,
                      fillColor: themeColors.primary,
                      color: themeColors.primaryText,
                      alignment: 'center',
                    },
                    {
                      text: 'Unit',
                      bold: true,
                      fillColor: themeColors.primary,
                      color: themeColors.primaryText,
                      alignment: 'center',
                    },
                    {
                      text: 'Unit Price',
                      bold: true,
                      fillColor: themeColors.primary,
                      color: themeColors.primaryText,
                      alignment: 'center',
                    },
                    {
                      text: 'Total',
                      bold: true,
                      fillColor: themeColors.primary,
                      color: themeColors.primaryText,
                      alignment: 'center',
                    },
                  ],
                  ...data.quotationItems.map(
                    (item: QuotationItem, index: number) => [
                      {
                        text: item.srNo,
                        fillColor:
                          index % 2 === 0 ? '#FFFFFF' : themeColors.accent,
                        alignment: 'center',
                      },
                      {
                        stack: [
                          {
                            text: item.description || '-',
                            bold: false,
                            fontSize: item.comments ? 8 : null,
                            margin: [0, 0, 0, 4],
                          },
                          ...(item.comments
                            ? [
                              {
                                text: `(Note: ${item.comments})`,
                                decoration: 'underline',
                                bold: true,
                                fontSize: 8,
                                margin: [0, 2, 0, 4],
                                noWrap: false,
                              },
                            ]
                            : []),
                        ],
                        fillColor:
                          index % 2 === 0 ? '#FFFFFF' : themeColors.accent,
                      },
                      {
                        text: item.quantity.toFixed(2),
                        fillColor:
                          index % 2 === 0 ? '#FFFFFF' : themeColors.accent,
                        alignment: 'right',
                      },
                      {
                        text: this.masterCommonService.getUnitName(item.unit)?.unitName || '-',
                        fillColor:
                          index % 2 === 0 ? '#FFFFFF' : themeColors.accent,
                        alignment: 'center',
                      },
                      {
                        text: this.getInvoiceUnitPriceText(item),
                        fillColor:
                          index % 2 === 0 ? '#FFFFFF' : themeColors.accent,
                        alignment:
                          item.unitPrice == null || item.unitPrice == 0 ? 'center' : 'right',
                      },
                      {
                        text: this.getInvoiceTotalPriceText(item),
                        fontSize: this.getInvoiceTotalPriceText(item).toLowerCase() == "regret" ? 9 : null,
                        bold: this.getInvoiceTotalPriceText(item).toLowerCase() == "regret" ? true : false,
                        italics: this.getInvoiceTotalPriceText(item).toLowerCase() == "regret" ? true : false,
                        fillColor:
                          index % 2 === 0 ? '#FFFFFF' : themeColors.accent,
                        alignment:
                          item.unitPrice == null ? 'center' : 'right',
                      },
                    ],
                  ),
                ],
              },
              margin: [0, 20, 0, 20],
              layout: {
                hLineWidth: (i, node) =>
                  i === 0 || i === node.table.body.length ? 1 : 0.5,
                vLineWidth: () => 0.5,
                hLineColor: themeColors.border,
                vLineColor: themeColors.border,
              },
            },
          ],
        },
        // Totals section - unbreakable
        {
          unbreakable: true,
          stack: [
            {
              columns: [
                {
                  width: '*',
                  stack: [
                    {
                      text: 'TOTAL IN WORDS:',
                      bold: true,
                      margin: [0, 0, 0, 5],
                      color: themeColors.primary,
                    },
                    {
                      text: InvoiceData.convertNumberToWords(
                        data.quotationFields.grandTotal,
                      ).toUpperCase(),
                      margin: [0, 0, 0, 15],
                    },
                    {
                      text: 'ADDITIONAL NOTES:',
                      bold: true,
                      margin: [0, 0, 0, 2],
                      color: themeColors.primary,
                    },
                    {
                      text: InvoiceData.ADDITIONALNOTES.toUpperCase() || 'N/A',
                      margin: [0, 0, 0, 15],
                    },
                  ],
                },
                {
                  width: 250,
                  stack: [
                    {
                      table: {
                        widths: ['*', '*'],
                        body: [
                          [
                            {
                              text: 'Net Total',
                              fillColor: themeColors.secondary,
                              bold: true,
                            },
                            {
                              text: data.quotationFields.netTotal.toFixed(2),
                              alignment: 'right',
                              bold: true,
                            },
                          ],
                          ...(data.quotationFields.isGstRemoved
                            ? []
                            : [
                              [
                                {
                                  text: 'GST',
                                  fillColor: themeColors.secondary,
                                  bold: true,
                                },
                                {
                                  text: data.quotationFields.gstAmount.toFixed(
                                    2,
                                  ),
                                  alignment: 'right',
                                  bold: true,
                                },
                              ],
                            ]),
                          ...(data.quotationFields?.discount == null || data.quotationFields?.discount === 0 ? [] :
                            [
                              [
                                {
                                  text: 'Discount',
                                  fillColor: themeColors.secondary,
                                  bold: true,
                                },
                                {
                                  text:
                                    (data.quotationFields?.discount?.toFixed(2) ??
                                      '0.00') +
                                    (data.quotationFields.discountType === 'percent'
                                      ? ' %'
                                      : ' Rs'),
                                  alignment: 'right',
                                  bold: true,
                                },
                              ],
                            ]),
                          ...(data.quotationFields.otherCharges == null || data.quotationFields.otherCharges === 0
                            ? []
                            : [
                              [
                                {
                                  text: 'P&F Charges',
                                  fillColor: themeColors.secondary,
                                  bold: true,
                                },
                                {
                                  text: data.quotationFields.otherCharges.toFixed(
                                    2,
                                  ),
                                  alignment: 'right',
                                  bold: true,
                                },
                              ],
                            ]),
                          [
                            {
                              text: data.quotationFields.isRoundOff
                                ? [
                                  { text: 'Grand Total', bold: true },
                                  {
                                    text: ' (rounded off)',
                                    italics: true,
                                    fontSize: 9,
                                    bold: true,
                                  },
                                ]
                                : 'Grand Total',
                              fillColor: themeColors.primary,
                              color: themeColors.primaryText,
                            },
                            {
                              text: data.quotationFields.grandTotal.toFixed(2),
                              alignment: 'right',
                              fillColor: themeColors.primary,
                              color: themeColors.primaryText,
                              bold: true,
                            },
                          ],
                        ],
                      },
                      layout: {
                        defaultBorder: false,
                        hLineWidth: (i, node) => 0.5,
                        vLineWidth: (i, node) => 0.5,
                        hLineColor: themeColors.border,
                        vLineColor: themeColors.border,
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },
        // Terms & Conditions - unbreakable
        // Footer - unbreakable
        {
          unbreakable: true,
          stack: [
            {
              columns: [
                // Left column - empty when not proforma

                // Middle column - Terms & Conditions when not proforma
                ...[
                  {
                    width: '*',
                    stack: [
                      termsAndConditionsSection
                        ? [termsAndConditionsSection]
                        : [],
                    ],
                  },
                ],
                // Right column - thank you note and contact info
                data.quotationFields.quotationStage.toLowerCase() !== 'proforma'
                  ? {
                    width:
                      data.quotationFields.quotationStage.toLowerCase() !==
                        'proforma'
                        ? 'auto'
                        : '*',
                    stack: [
                      {
                        text:
                          'Thank you for your business\nFor Arihant Tubes & Fittings',
                        alignment: 'right',
                        margin: [0, 0, 0, 10],
                        bold: true,
                        color: themeColors.primary,
                      },
                      {
                        image: logoBase64,
                        width: 100,
                        alignment: 'right',
                        margin: [0, 0, 0, 10],
                      },
                      {
                        text: `Name: ${this.masterCommonService.getAssignedToName(
                          data.quotationFields.quotationAssignedToId,
                        )?.username || '-'
                          }`,
                        alignment: 'right',
                        margin: [0, 0, 0, 5],
                      },
                      {
                        text: `Email Id: ${this.masterCommonService.getAssignedToName(
                          data.quotationFields.quotationAssignedToId,
                        )?.emailId || '-'
                          }`,
                        alignment: 'right',
                        margin: [0, 0, 0, 5],
                      },
                      {
                        text: `Mobile No: ${this.masterCommonService.getAssignedToName(
                          data.quotationFields.quotationAssignedToId,
                        )?.mobileNo || '-'
                          }`,
                        alignment: 'right',
                      },
                    ],
                  }
                  : [],
              ],
            },
          ],
        },
        // Bank Details (conditionally shown for proforma) - unbreakable
        data.quotationFields.quotationStage.toLowerCase() === 'proforma'
          ? [
            {
              unbreakable: true,
              stack: [
                {
                  margin: [0, 3, 0, 0],
                  columns: [
                    {
                      table: {
                        widths: ['*'],
                        body: [
                          [
                            {
                              text: 'BANK DETAILS',
                              bold: true,
                              color: themeColors.primary,
                            },
                          ],
                          ...Object.entries(InvoiceData.BANKDETAILS).map(
                            ([key, value]) => [
                              {
                                text: [
                                  { text: `${key}: `, bold: true, fontSize: 10 },
                                  { text: `${value}`, fontSize: 9 },
                                ],
                                margin: [0, 2, 0, 2],
                              },
                            ],
                          ),
                        ],
                      },
                      layout: 'noBorders',
                    },
                    data.quotationFields.quotationStage.toLowerCase() ===
                      'proforma'
                      ? {
                        width: 'auto',
                        stack: [
                          {
                            text:
                              'Thank you for your business\nFor Arihant Tubes & Fittings',
                            alignment: 'right',
                            margin: [0, 0, 0, 10],
                            bold: true,
                            color: themeColors.primary,
                          },
                          {
                            image: logoBase64,
                            width: 100,
                            alignment: 'right',
                            margin: [0, 0, 0, 10],
                          },
                          {
                            text: `Name: ${this.masterCommonService.getAssignedToName(
                              data.quotationFields.quotationAssignedToId,
                            )?.username || '-'
                              }`,
                            alignment: 'right',
                            margin: [0, 0, 0, 5],
                          },
                          {
                            text: `Email Id: ${this.masterCommonService.getAssignedToName(
                              data.quotationFields.quotationAssignedToId,
                            )?.emailId || '-'
                              }`,
                            alignment: 'right',
                            margin: [0, 0, 0, 5],
                          },
                          {
                            text: `Mobile No: ${this.masterCommonService.getAssignedToName(
                              data.quotationFields.quotationAssignedToId,
                            )?.mobileNo || '-'
                              }`,
                            alignment: 'right',
                          },
                        ],
                      }
                      : [],
                  ],
                },
              ],
            },
          ]
          : [],
        // Footer - unbreakable
      ],
      defaultStyle: {
        fontSize: 11,
      },
      pageMargins: [40, 40, 40, 60], // Added bottom margin for footer
      footer: function (currentPage: number, pageCount: number) {
        return {
          columns: [
            { text: '' },
            {
              text: `Page ${currentPage} of ${pageCount}`,
              alignment: 'right',
              fontSize: 9,
              margin: [0, 0, 40, 0],
              color: '#888888',
            },
          ],
        }
      },
    }

    // Create and open the PDF
    pdfMake.createPdf(docDefinition).open()
  }

  getInvoiceUnitPriceText(item: QuotationItem): string {
    if (item.unitPrice == null || item.unitPrice == 0) {
      return '-'
    }
    return Number(item.unitPrice).toFixed(2)
  }

  getInvoiceTotalPriceText(item: QuotationItem): string {
    if (item.unitPrice == null) {
      return '-'
    }
    if (Number(item.unitPrice) === 0) {
      return 'REGRET'
    }
    return Number(item.totalPrice).toFixed(2)
  }
}
